import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../../components/Toast'
import { usuarioService } from '../../../services/usuario'
import { userQueryKeys, setorQueryKeys } from './useUserQueries'
import type { CreateUserRequest, UpdateUserRequest } from '../../../services/usuario'

/**
 * Mutation para criar usuário
 */
export const useCreateUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateUserRequest) => {
      return usuarioService.create(data)
    },

    onSuccess: () => {
      // Invalidar e refetch
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() })
      toast.success('Usuário criado com sucesso!')
    },

    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || 'Erro ao criar usuário'
      toast.error(message)
    },
  })
}

/**
 * Mutation para atualizar usuário
 */
export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateUserRequest }) => {
      return usuarioService.update(id, data)
    },

    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: userQueryKeys.detail(updatedUser.id) })
      toast.success('Usuário atualizado com sucesso!')
    },

    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || 'Erro ao atualizar usuário'
      toast.error(message)
    },
  })
}

/**
 * Mutation para desativar usuário (soft delete)
 */
export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      return usuarioService.delete(id)
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() })
      toast.success('Usuário desativado com sucesso!')
    },

    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || 'Erro ao desativar usuário'
      toast.error(message)
    },
  })
}

/**
 * Mutation para vincular usuários a um setor
 */
export const useVincularUsuariosMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ setorId, usuariosIds }: { setorId: number; usuariosIds: number[] }) => {
      return usuarioService.vincularUsuarios(setorId, usuariosIds)
    },

    onSuccess: async () => {
      // Invalidar e aguardar refetch para garantir dados atualizados
      await queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: setorQueryKeys.lists() })
      toast.success('Setores vinculados com sucesso!')
    },

    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || 'Erro ao vincular setores'
      toast.error(message)
    },
  })
}

/**
 * Mutation para desvincular usuário de um setor
 */
export const useDesvincularUsuarioMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ usuarioId, setorId }: { usuarioId: number; setorId: number }) => {
      return usuarioService.desvincularUsuario(usuarioId, setorId)
    },

    onSuccess: async () => {
      // Invalidar e aguardar refetch para garantir dados atualizados
      await queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: setorQueryKeys.lists() })
      toast.success('Setor desvinculado com sucesso!')
    },

    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || 'Erro ao desvincular setor'
      toast.error(message)
    },
  })
}
